import Box from '@mui/material/Box'
import Icon from '@mui/material/Icon'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { pathOr } from 'ramda'
import React from 'react'

import { type SchemaGroup } from '@/__generated/es-api'
import { Checkbox as FormCheckbox } from '@/app/component/atom/form'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { usePaginated } from '@/app/hooks/use-paginated'
import { useSearchable } from '@/app/hooks/use-searchable'
import { useSubscribeGroups } from '@/app/module/campaigns/hooks/use-subscribe-groups'
import { useUnsubscribeGroups } from '@/app/module/campaigns/hooks/use-unsubscribe-groups'
import { type CampaignItem, type Delta } from '@/app/module/campaigns/types'
import { toDelay } from '@/app/module/campaigns/utils/delay'
import { useGetGroups } from '@/app/module/groups/hooks/use-get-groups'
import { useGetSegments } from '@/app/module/segments/hooks/use-get-segments'
import { type ChangeEvent, type CountryTimezone, type Timezones } from '@/app/types'

import DelayDialogContent, { type DelaySubscriptionPayload } from './delay-dialog'
import { ResponsiveGroupSubTable, ResponsiveSegmentSubTable } from './responsive-sub-tables'

type GroupSegmentQuery = {
  page: number
  searchTerm: string
  size: number
}

type GroupSegmentData = Record<number, SchemaGroup>

type Props = {
  campaign: CampaignItem
  campaignId: number
  countriesTimezones: CountryTimezone[]
  countryCode: string
  orgId: number
  timezone: string
  timezones: Timezones
}

type GroupSegmentProps = Props & {
  root: 'groups' | 'segments'
}

const GroupSegmentSubscriptionsTable: React.FC<GroupSegmentProps> = (props) => {
  const { campaign, campaignId, orgId, root, timezone, timezones, countriesTimezones, countryCode } = props
  const [subscribeGroups, { isLoading: isSubscribingGroups }] = useSubscribeGroups()
  const [unsubscribeGroups, { isLoading: isUnsubscribingGroups }] = useUnsubscribeGroups()
  const isSegment = root === 'segments'

  const [query, setQuery] = React.useState(defaultQuery)
  const apiQuery = React.useMemo(
    () => ({
      page: query.page,
      search: query.searchTerm,
      size: query.size,
    }),
    [query.page, query.searchTerm, query.size],
  )
  const groupsResponse = useGetGroups(apiQuery, { skip: root !== 'groups' })
  const segmentsResponse = useGetSegments(apiQuery, { skip: root !== 'segments' })
  const response = root === 'groups' ? groupsResponse : segmentsResponse
  const rows = React.useMemo(() => response.data?.groups || [], [response.data?.groups])
  const data = React.useMemo<GroupSegmentData>(
    () =>
      rows.reduce<GroupSegmentData>((acc, item) => {
        acc[item.id] = item
        return acc
      }, {}),
    [rows],
  )
  const [selectedGroupIds, setSelectedGroupIds] = React.useState<number[]>([])
  const [selectedGroupsById, setSelectedGroupsById] = React.useState<GroupSegmentData>({})
  const selectedData = React.useMemo(() => ({ ...selectedGroupsById, ...data }), [data, selectedGroupsById])
  const loading = response.isLoading || response.isFetching
  const isLoading = loading || isSubscribingGroups || isUnsubscribingGroups
  const total = response.data?.total || 0
  const reset = React.useCallback(() => setQuery(defaultQuery), [])
  const handleGetData = React.useCallback(async (nextQuery: GroupSegmentQuery) => setQuery(nextQuery), [])
  const handleReload = React.useCallback(() => {
    response.refetch()
  }, [response])
  const setPageSize = React.useCallback((pageSize: number) => {
    setQuery((state) => ({
      ...state,
      page: 1,
      size: pageSize,
    }))
  }, [])
  const pagination = usePaginated({
    query,
    root,
    total,
    getData: handleGetData,
    navigate: setQuery,
    setPageSize,
  })
  const search = useSearchable<GroupSegmentQuery>({
    orgId,
    pageSearchTerm: '',
    query,
    getData: handleGetData,
    navigate: setQuery,
    reset,
  })

  const [processingGroupIds, setProcessingGroupIds] = React.useState<number[]>([])
  const createConfirmation = useConfirmation()

  const withProcessingGroupIds = React.useCallback(async (groupIds: number[], callback: () => Promise<unknown>) => {
    setProcessingGroupIds((current) => Array.from(new Set([...current, ...groupIds])))
    try {
      await callback()
    } finally {
      setProcessingGroupIds((current) => current.filter((id) => !groupIds.includes(id)))
    }
  }, [])

  const getSubscribeTitle = React.useCallback(
    (groupIds: number[]) => {
      if (groupIds.length > 1) {
        return (
          <>
            Subscribe {groupIds.length} {isSegment ? 'Segments' : 'Groups'}
          </>
        )
      }

      const groupId = groupIds[0]
      return (
        <>
          Subscribe {pathOr('', [groupId, 'name'], selectedData)} (<SmallGroupIcon />
          {pathOr('', [groupId, 'numberOfContacts'], selectedData)})
        </>
      )
    },
    [isSegment, selectedData],
  )

  const getUnsubscribeTitle = React.useCallback(
    (groupIds: number[]) => {
      if (groupIds.length > 1) {
        return (
          <>
            Unsubscribe {groupIds.length} {isSegment ? 'Segments' : 'Groups'}
          </>
        )
      }

      const groupId = groupIds[0]
      return (
        <>
          Unsubscribe {pathOr('', [groupId, 'name'], selectedData)} (<SmallGroupIcon />
          {pathOr('', [groupId, 'numberOfContacts'], selectedData)})
        </>
      )
    },
    [isSegment, selectedData],
  )

  const clearSelection = React.useCallback(() => {
    setSelectedGroupIds([])
    setSelectedGroupsById({})
  }, [])

  const handleSelectGroup = React.useCallback(
    (groupId: number, checked: boolean) => {
      setSelectedGroupIds((current) => {
        if (!checked) {
          return current.filter((id) => id !== groupId)
        }

        return current.includes(groupId) ? current : [...current, groupId]
      })
      setSelectedGroupsById((current) => {
        if (!checked) {
          const { [groupId]: _removedGroup, ...nextSelected } = current
          return nextSelected
        }

        return data[groupId] ? { ...current, [groupId]: data[groupId] } : current
      })
    },
    [data],
  )

  const openSubscribeConfirmation = React.useCallback(
    async ({
      groupIds,
      delay,
      startAt,
      t0,
      t0timezone,
    }: {
      groupIds: number[]
      delay?: Omit<Delta, 'timezones' | 'valueError'>
      startAt?: string
      t0?: string
      t0timezone?: string
    }) => {
      let allowResubscribe = false
      let confirmValue = ''

      const confirmed = await createConfirmation({
        title: getSubscribeTitle(groupIds),
        content: ({ setConfirmButtonDisabled }) => (
          <SubscribeConfirmationContent
            data={selectedData}
            groupIds={groupIds}
            root={root}
            onConfirmValueChange={(value) => {
              confirmValue = value
            }}
            onResubscribeChange={(value) => {
              allowResubscribe = value
            }}
            setConfirmButtonDisabled={setConfirmButtonDisabled}
          />
        ),
        confirmButtonLabel: 'Confirm',
        confirmButtonDisabled: true,
        cancelButtonLabel: 'Cancel',
        hideCancel: true,
      })

      const expectedCount = `${getTotalContacts(selectedData, groupIds)}`

      if (confirmed && confirmValue && confirmValue === expectedCount) {
        await withProcessingGroupIds(groupIds, () =>
          subscribeGroups(campaignId, {
            allowResubscribe,
            delay: delay && toDelay(delay),
            groupIds,
            isSegment,
            startAt,
            t0,
            t0timezone,
          }),
        )
        clearSelection()
      }
    },
    [
      campaignId,
      isSegment,
      root,
      selectedData,
      clearSelection,
      createConfirmation,
      getSubscribeTitle,
      subscribeGroups,
      withProcessingGroupIds,
    ],
  )

  const handleSubscribe = React.useCallback(
    async (groupIds: number[]) => {
      if (groupIds.length === 0) {
        return
      }

      let subscriptionPayload: DelaySubscriptionPayload = {}

      const confirmed = await createConfirmation({
        title: getSubscribeTitle(groupIds),
        content: ({ setConfirmButtonDisabled }) => (
          <DelayDialogContent
            campaign={campaign}
            countriesTimezones={countriesTimezones}
            countryCode={countryCode}
            timezone={timezone}
            timezones={timezones}
            onChange={(payload) => {
              subscriptionPayload = payload
            }}
            setConfirmButtonDisabled={setConfirmButtonDisabled}
          />
        ),
        confirmButtonLabel: 'Continue',
        cancelButtonLabel: 'Cancel',
        icon: 'input',
        fullWidth: true,
        maxWidth: 'sm',
      })

      if (confirmed) {
        await openSubscribeConfirmation({
          groupIds,
          delay: subscriptionPayload.delay,
          startAt: subscriptionPayload.startAt,
          t0: subscriptionPayload.t0,
          t0timezone: subscriptionPayload.t0timezone,
        })
      }
    },
    [
      campaign,
      countriesTimezones,
      countryCode,
      timezone,
      timezones,
      createConfirmation,
      getSubscribeTitle,
      openSubscribeConfirmation,
    ],
  )

  const handleUnsubscribe = React.useCallback(
    async (groupIds: number[]) => {
      if (groupIds.length === 0) {
        return
      }

      const confirmed = await createConfirmation({
        title: getUnsubscribeTitle(groupIds),
        content: <UnsubscribeConfirmationContent data={selectedData} groupIds={groupIds} root={root} />,
        confirmButtonLabel: 'Confirm',
        cancelButtonLabel: 'Cancel',
        hideCancel: true,
      })

      if (confirmed) {
        await withProcessingGroupIds(groupIds, () =>
          unsubscribeGroups(campaignId, {
            groupIds,
            isSegment,
          }),
        )
        clearSelection()
      }
    },
    [
      campaignId,
      isSegment,
      root,
      selectedData,
      clearSelection,
      createConfirmation,
      getUnsubscribeTitle,
      unsubscribeGroups,
      withProcessingGroupIds,
    ],
  )

  const itemLabel = isSegment ? 'segment' : 'group'
  const selectionDisabledReason = getSelectionDisabledReason({
    campaignOpen: campaign.open,
    campaignPublished: campaign.published,
    isLoading,
    itemLabel,
  })
  const ResponsiveSubTable = isSegment ? ResponsiveSegmentSubTable : ResponsiveGroupSubTable

  return (
    <ResponsiveSubTable
      campaign={campaign}
      groups={rows}
      isLoading={isLoading}
      pagination={pagination}
      processingGroupIds={processingGroupIds}
      refreshDisabled={isLoading}
      searchTerm={search.searchTerm}
      selectedGroupIds={selectedGroupIds}
      selectedGroupsById={selectedGroupsById}
      selectionDisabledReason={selectionDisabledReason}
      timezone={timezone}
      onClearSelection={clearSelection}
      onRefresh={handleReload}
      onSearch={search.onSubmit}
      onSelectGroup={handleSelectGroup}
      onSubscribe={handleSubscribe}
      onUnsubscribe={handleUnsubscribe}
    />
  )
}

const SmallGroupIcon = ({ fontSize = 'small' }: { fontSize?: 'inherit' | 'large' | 'medium' | 'small' }) => (
  <Icon
    fontSize={fontSize}
    style={{
      verticalAlign: 'middle',
      marginRight: '2px',
      marginLeft: '1px',
    }}
  >
    group
  </Icon>
)

type SubscribeConfirmationContentProps = {
  data: GroupSegmentData
  groupIds: number[]
  root: 'groups' | 'segments'
  onConfirmValueChange: (value: string) => void
  onResubscribeChange: (value: boolean) => void
  setConfirmButtonDisabled: (disabled: boolean) => void
}

const SubscribeConfirmationContent: React.FC<SubscribeConfirmationContentProps> = ({
  data,
  groupIds,
  root,
  onConfirmValueChange,
  onResubscribeChange,
  setConfirmButtonDisabled,
}) => {
  const [resubscribe, setResubscribe] = React.useState(false)
  const [confirmValue, setConfirmValue] = React.useState('')

  const expectedCount = `${getTotalContacts(data, groupIds)}`
  const isBulk = groupIds.length > 1
  const itemLabel = getGroupSegmentLabel(root, isBulk)
  const itemLabelTitle = getGroupSegmentLabelTitle(root, isBulk)
  const itemSpecifier = isBulk ? `these ${itemLabel}` : `this ${itemLabel}`

  React.useEffect(() => {
    setConfirmButtonDisabled(confirmValue === '' || confirmValue !== expectedCount)
  }, [confirmValue, expectedCount, setConfirmButtonDisabled])

  const description = getSubscribeConfirmationDescription(isBulk, root, itemLabel)
  const resubscribeLabel = getResubscribeLabel(itemSpecifier)

  return (
    <Box sx={{ color: 'text.secondary', typography: 'body1' }}>
      <Typography color="inherit" variant="body1" paragraph>
        {description}
        <br />
        <br />
        <strong>
          Contacts previously subscribed to this campaign will not be subscribed again unless you check the box below.
        </strong>
      </Typography>
      <FormCheckbox
        label={
          <Typography component="span" variant="body1">
            {resubscribeLabel}
          </Typography>
        }
        name="resubscribe"
        value={resubscribe.toString()}
        editable
        onChange={(e: ChangeEvent<string>) => {
          const nextValue = e.value === 'true'
          setResubscribe(nextValue)
          onResubscribeChange(nextValue)
        }}
      />
      <Box sx={{ mt: 2 }}>
        <Typography color="inherit" variant="body1">
          You are about to subscribe the following {itemLabel}:
        </Typography>
        <Box
          component="ul"
          aria-label={`${itemLabelTitle} to subscribe`}
          sx={{ marginBottom: 1, marginTop: 1, paddingLeft: 3 }}
        >
          {groupIds.map((groupId) => (
            <Box component="li" key={groupId} sx={{ marginBottom: 0.5 }}>
              <Typography color="inherit" component="span" variant="body1">
                {pathOr('', [groupId, 'name'], data)} ({pathOr(0, [groupId, 'numberOfContacts'], data)} contacts)
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography color="inherit" variant="body1" sx={{ fontWeight: 700, marginBottom: 1 }}>
          Total: up to {expectedCount} contacts
        </Typography>
        <Typography color="inherit" variant="body1" sx={{ fontWeight: 700 }}>
          To confirm you want to subscribe the contacts in {itemSpecifier}, please type "{expectedCount}", then press
          Confirm.
        </Typography>
      </Box>
      <Box sx={{ marginTop: '10px' }}>
        <TextField
          placeholder={`Confirm ${itemLabelTitle} Size`}
          size="small"
          fullWidth
          variant="outlined"
          value={confirmValue}
          className="subscription-contacts-confirm-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon>group</Icon>
              </InputAdornment>
            ),
          }}
          error={confirmValue !== expectedCount}
          onChange={(e) => {
            const value = e.currentTarget.value
            setConfirmValue(value)
            onConfirmValueChange(value)
          }}
        />
      </Box>
    </Box>
  )
}

type UnsubscribeConfirmationContentProps = {
  data: GroupSegmentData
  groupIds: number[]
  root: 'groups' | 'segments'
}

const UnsubscribeConfirmationContent: React.FC<UnsubscribeConfirmationContentProps> = ({ data, groupIds, root }) => {
  const isBulk = groupIds.length > 1
  const itemLabel = getGroupSegmentLabel(root, isBulk)
  const itemLabelTitle = getGroupSegmentLabelTitle(root, isBulk)
  const totalContacts = getTotalContacts(data, groupIds)

  return (
    <Box sx={{ color: 'text.secondary', typography: 'body1' }}>
      <Typography color="inherit" variant="body1">
        All contacts currently in {isBulk ? `these ${itemLabel}` : `this ${itemLabel}`} who are subscribed to this
        campaign will be unsubscribed from the campaign.
        <br />
        <br />
        <strong>
          Contacts not currently in {isBulk ? `these ${itemLabel}` : `this ${itemLabel}`} will not be unsubscribed, even
          if they were in {isBulk ? `these ${itemLabel}` : `this ${itemLabel}`} previously.
        </strong>
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Typography color="inherit" variant="body1">
          You are about to unsubscribe contacts from the following {itemLabel}:
        </Typography>
        <Box
          component="ul"
          aria-label={`${itemLabelTitle} to unsubscribe`}
          sx={{ marginBottom: 1, marginTop: 1, paddingLeft: 3 }}
        >
          {groupIds.map((groupId) => (
            <Box component="li" key={groupId} sx={{ marginBottom: 0.5 }}>
              <Typography color="inherit" component="span" variant="body1">
                {pathOr('', [groupId, 'name'], data)} ({pathOr(0, [groupId, 'numberOfContacts'], data)} contacts)
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography color="inherit" variant="body1" sx={{ fontWeight: 700 }}>
          Total: up to {totalContacts} contacts
        </Typography>
      </Box>
    </Box>
  )
}

const defaultQuery: GroupSegmentQuery = {
  page: 1,
  size: 25,
  searchTerm: '',
}

const getLoadingDisabledReason = (isLoading: boolean) =>
  isLoading ? 'Please wait until the current subscription action is finished.' : ''

const getSelectionDisabledReason = ({
  campaignOpen,
  campaignPublished,
  isLoading,
  itemLabel,
}: {
  campaignOpen: boolean
  campaignPublished: boolean
  isLoading: boolean
  itemLabel: string
}) => {
  if (!campaignOpen && !campaignPublished) {
    return `Campaign has to be opened to be able to select ${itemLabel}s.`
  }

  return getLoadingDisabledReason(isLoading)
}

const getSubscribeConfirmationDescription = (isBulk: boolean, root: 'groups' | 'segments', pluralLabel: string) => {
  if (isBulk) {
    return (
      <>
        All contacts currently in these {pluralLabel} will be subscribed to the campaign. If contacts are later added to
        the {pluralLabel}, they will not automatically be subscribed to the campaign. You will need to subscribe the{' '}
        {pluralLabel} again to subscribe those new contacts.
      </>
    )
  }

  if (root === 'segments') {
    return (
      <>
        All contacts currently in this segment will be subscribed to the campaign. If contacts are later added to the
        segment, they will not automatically be subscribed to the campaign. You will need to subscribe the segment again
        to subscribe those new contacts.
      </>
    )
  }

  return (
    <>
      All contacts currently in this group will be subscribed to the campaign. If you later add contacts to the group,
      they will not automatically be subscribed to the campaign; you will need to subscribe the group again to subscribe
      those new contacts.
    </>
  )
}

const getResubscribeLabel = (itemSpecifier: string) =>
  `Subscribe all contacts in ${itemSpecifier}, even those who have been previously subscribed.`

const getTotalContacts = (data: GroupSegmentData, groupIds: number[]) =>
  groupIds.reduce((total, groupId) => total + (data[groupId]?.numberOfContacts || 0), 0)

const getGroupSegmentLabel = (root: 'groups' | 'segments', isBulk: boolean) => {
  if (root === 'segments') {
    return isBulk ? 'segments' : 'segment'
  }

  return isBulk ? 'groups' : 'group'
}

const getGroupSegmentLabelTitle = (root: 'groups' | 'segments', isBulk: boolean) => {
  if (root === 'segments') {
    return isBulk ? 'Segments' : 'Segment'
  }

  return isBulk ? 'Groups' : 'Group'
}

const GroupSubscriptionsTable: React.FC<Props> = (props) => <GroupSegmentSubscriptionsTable {...props} root="groups" />

const SegmentSubscriptionsTable: React.FC<Props> = (props) => (
  <GroupSegmentSubscriptionsTable {...props} root="segments" />
)

export { GroupSubscriptionsTable, SegmentSubscriptionsTable }
